import { FC, useMemo } from 'react';
import type { AssessmentProgressEvent } from '@libs/types';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { STAGE_LABEL_KEYS, timelineOffset } from '../../utils/run-progress';

/**
 * The activity feed.
 *
 * Newest first, which is why the API's 200-event cap trims the OLDEST entries:
 * the ones a reader is looking at are always at the top. Not paged with the
 * document list below it — the feed is the run's whole history and would stop
 * being a history if it followed the document pager.
 *
 * Every line is built here from the event's enum fields, never from a string the
 * AI sent. That is what makes the feed translatable — and it is why the wire
 * carries `state: "graded"` plus two numbers instead of a sentence.
 */

interface TimelineLine {
  key: string;
  time: string;
  text: string;
  kicker: string;
  dot: string;
}

export const ActivityTimeline: FC<{
  events: AssessmentProgressEvent[];
  firstEventAt: string | undefined;
}> = observer(({ events, firstEventAt }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const lines = useMemo<TimelineLine[]>(() => {
    const stageChange = t('app.assessment.progress.timeline.stageChange');

    return events
      .map((event): TimelineLine | null => {
        const time = timelineOffset(event.occurredAt, firstEventAt);

        if (event.kind === 'stage') {
          const stageKey = event.stageKey ? STAGE_LABEL_KEYS[event.stageKey] : undefined;
          if (!stageKey) return null;
          const stage = t(stageKey);
          const text =
            event.state === 'completed'
              ? t('app.assessment.progress.timeline.stageCompleted', { stage })
              : event.state === 'failed'
                ? t('app.assessment.progress.timeline.stageFailed', { stage })
                : stage;
          return {
            key: `${event.seq}-${event.subjectId}-${event.state}`,
            time,
            text,
            kicker: stageChange,
            dot: event.state === 'failed' ? theme.palette.error.main : theme.palette.ocean[700],
          };
        }

        // Resolved server-side. It used to be looked up against the document
        // rows in the same response — which became one PAGE, so every line whose
        // document sat on another page lost its name and was dropped. Null here
        // now means only what it should: the document was deleted after the run
        // reported on it.
        const document = event.subjectName;
        if (!document) return null;

        const detail = event.detail ?? {};
        const met = typeof detail.questionsMet === 'number' ? detail.questionsMet : null;
        const total = typeof detail.questionsTotal === 'number' ? detail.questionsTotal : null;

        const text = ((): string | null => {
          switch (event.state) {
            case 'reading':
              return t('app.assessment.progress.timeline.reading', { document });
            case 'read':
              return t('app.assessment.progress.timeline.read', { document });
            case 'classified':
              return t('app.assessment.progress.timeline.classified', { document });
            case 'grading':
              return t('app.assessment.progress.timeline.grading', { document });
            case 'graded':
              return met != null && total != null
                ? t('app.assessment.progress.timeline.graded', { document, met, total })
                : t('app.assessment.progress.timeline.gradedPlain', { document });
            case 'failed':
              return t('app.assessment.progress.timeline.failed', { document });
            default:
              return null;
          }
        })();

        if (!text) return null;

        return {
          key: `${event.seq}-${event.subjectId}-${event.state}`,
          time,
          text,
          kicker: '',
          dot:
            event.state === 'failed'
              ? theme.palette.error.main
              : event.state === 'graded' || event.state === 'read'
                ? theme.palette.accent.green
                : theme.palette.grey[600],
        };
      })
      .filter((line): line is TimelineLine => line !== null);
  }, [events, firstEventAt, t, theme]);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.25,
          py: 1.5,
          backgroundColor: theme.palette.grey[200],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          {t('app.assessment.progress.activity')}
        </Typography>
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
          {t('app.assessment.progress.newestFirst')}
        </Typography>
      </Box>

      <Box sx={{ px: 2.25, pt: 0.5, pb: 2, maxHeight: 420, overflowY: 'auto' }}>
        {lines.length === 0 && (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', py: 2 }}>
            {t('app.assessment.progress.timeline.noActivity')}
          </Typography>
        )}

        {lines.map((line) => (
          <Box
            key={line.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 10px 1fr',
              gap: 1.25,
              alignItems: 'start',
              py: 1.375,
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: 'text.tertiary',
                fontVariantNumeric: 'tabular-nums',
                pt: 0.375,
                whiteSpace: 'nowrap',
              }}
            >
              {line.time}
            </Typography>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                mt: 0.5,
                flexShrink: 0,
                backgroundColor: line.dot,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              {line.kicker && (
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: theme.palette.ocean[700],
                    mb: 0.125,
                  }}
                >
                  {line.kicker}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>{line.text}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
});
